import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';
import { PageSEO } from '@/components/seo/SEOHelmet';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/i18n';

const TermsPage = () => {
    const { language } = useLanguage();
    
    const content = {
        sections: {
            acceptance: {
                title: {
                    en: 'Acceptance of Terms',
                    it: 'Accettazione dei Termini',
                    fr: 'Acceptation des Conditions',
                    es: 'Aceptación de los Términos',
                    de: 'Annahme der Bedingungen'
                },
                text: {
                    en: 'By accessing and using Sous Chef Linguine, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use our service.',
                    it: 'Accedendo e utilizzando Sous Chef Linguine, accetti e acconsenti a essere vincolato dai termini e dalle disposizioni di questo accordo. Se non accetti questi termini, ti preghiamo di non utilizzare il nostro servizio.',
                    fr: 'En accédant et en utilisant Sous Chef Linguine, vous acceptez et consentez à être lié par les termes et dispositions de cet accord. Si vous n\'acceptez pas ces conditions, veuillez ne pas utiliser notre service.',
                    es: 'Al acceder y usar Sous Chef Linguine, usted acepta y consiente estar sujeto a los términos y disposiciones de este acuerdo. Si no está de acuerdo con estos términos, por favor no use nuestro servicio.',
                    de: 'Durch den Zugang und die Nutzung von Sous Chef Linguine akzeptieren Sie und stimmen zu, an die Bedingungen und Bestimmungen dieser Vereinbarung gebunden zu sein. Wenn Sie diesen Bedingungen nicht zustimmen, nutzen Sie bitte unseren Dienst nicht.'
                }
            },
            description: {
                title: {
                    en: 'Description of Service',
                    it: 'Descrizione del Servizio',
                    fr: 'Description du Service',
                    es: 'Descripción del Servicio',
                    de: 'Beschreibung des Dienstes'
                },
                text: {
                    en: 'Sous Chef Linguine is an authentic global recipe platform that provides traditional recipes from around the world. Our service includes recipe discovery, AI-powered recipe generation, user ratings, and recipe collections.',
                    it: 'Sous Chef Linguine è una piattaforma di ricette globali autentiche che fornisce ricette tradizionali da tutto il mondo. Il nostro servizio include scoperta di ricette, generazione di ricette con IA, valutazioni degli utenti e collezioni di ricette.',
                    fr: 'Sous Chef Linguine est une plateforme de recettes mondiales authentiques qui fournit des recettes traditionnelles du monde entier. Notre service comprend la découverte de recettes, la génération de recettes par IA, les évaluations des utilisateurs et les collections de recettes.',
                    es: 'Sous Chef Linguine es una plataforma de recetas globales auténticas que proporciona recetas tradicionales de todo el mundo. Nuestro servicio incluye descubrimiento de recetas, generación de recetas con IA, calificaciones de usuarios y colecciones de recetas.',
                    de: 'Sous Chef Linguine ist eine authentische globale Rezeptplattform, die traditionelle Rezepte aus aller Welt bereitstellt. Unser Dienst umfasst Rezeptentdeckung, KI-gestützte Rezeptgenerierung, Nutzerbewertungen und Rezeptsammlungen.'
                }
            },
            aiDisclaimer: {
                title: {
                    en: 'AI-Generated Content Disclaimer',
                    it: 'Avvertenza sui Contenuti Generati dall\'IA',
                    fr: 'Avertissement sur le Contenu Généré par l\'IA',
                    es: 'Descargo de Responsabilidad sobre Contenido Generado por IA',
                    de: 'Haftungsausschluss für KI-Generierte Inhalte'
                },
                intro: {
                    en: 'Some recipes on our platform may be generated by Sous-Chef Linguine AI using traditional authenticity ranking systems. While we strive for accuracy:',
                    it: 'Alcune ricette sulla nostra piattaforma potrebbero essere generate dall\'IA Sous-Chef Linguine utilizzando sistemi di classificazione dell\'autenticità tradizionale. Pur sforzandoci per l\'accuratezza:',
                    fr: 'Certaines recettes sur notre plateforme peuvent être générées par l\'IA Sous-Chef Linguine utilisant des systèmes de classement d\'authenticité traditionnels. Bien que nous visions la précision :',
                    es: 'Algunas recetas en nuestra plataforma pueden ser generadas por la IA Sous-Chef Linguine utilizando sistemas de clasificación de autenticidad tradicional. Aunque nos esforzamos por la precisión:',
                    de: 'Einige Rezepte auf unserer Plattform können von der Sous-Chef Linguine KI unter Verwendung traditioneller Authentizitätsbewertungssysteme generiert werden. Obwohl wir nach Genauigkeit streben:'
                },
                items: {
                    en: ['AI-generated recipes are marked as such', 'We do not guarantee the historical accuracy of all content', 'Users should verify allergen information independently', 'Cooking results may vary based on ingredients and techniques used'],
                    it: ['Le ricette generate dall\'IA sono contrassegnate come tali', 'Non garantiamo l\'accuratezza storica di tutti i contenuti', 'Gli utenti dovrebbero verificare indipendentemente le informazioni sugli allergeni', 'I risultati della cottura possono variare in base agli ingredienti e alle tecniche utilizzate'],
                    fr: ['Les recettes générées par l\'IA sont marquées comme telles', 'Nous ne garantissons pas l\'exactitude historique de tout le contenu', 'Les utilisateurs doivent vérifier indépendamment les informations sur les allergènes', 'Les résultats de cuisson peuvent varier selon les ingrédients et techniques utilisés'],
                    es: ['Las recetas generadas por IA están marcadas como tales', 'No garantizamos la precisión histórica de todo el contenido', 'Los usuarios deben verificar la información de alérgenos de forma independiente', 'Los resultados de cocción pueden variar según los ingredientes y técnicas utilizadas'],
                    de: ['KI-generierte Rezepte sind als solche gekennzeichnet', 'Wir garantieren nicht die historische Genauigkeit aller Inhalte', 'Benutzer sollten Allergeninformationen unabhängig überprüfen', 'Kochergebnisse können je nach verwendeten Zutaten und Techniken variieren']
                }
            },
            userAccounts: {
                title: {
                    en: 'User Accounts',
                    it: 'Account Utente',
                    fr: 'Comptes Utilisateurs',
                    es: 'Cuentas de Usuario',
                    de: 'Benutzerkonten'
                },
                items: {
                    en: ['You are responsible for maintaining the confidentiality of your account', 'You must provide accurate information when creating an account', 'You may not use another person\'s account without permission', 'We reserve the right to suspend accounts that violate these terms'],
                    it: ['Sei responsabile del mantenimento della riservatezza del tuo account', 'Devi fornire informazioni accurate quando crei un account', 'Non puoi utilizzare l\'account di un\'altra persona senza permesso', 'Ci riserviamo il diritto di sospendere gli account che violano questi termini'],
                    fr: ['Vous êtes responsable du maintien de la confidentialité de votre compte', 'Vous devez fournir des informations exactes lors de la création d\'un compte', 'Vous ne pouvez pas utiliser le compte d\'une autre personne sans permission', 'Nous nous réservons le droit de suspendre les comptes qui violent ces conditions'],
                    es: ['Usted es responsable de mantener la confidencialidad de su cuenta', 'Debe proporcionar información precisa al crear una cuenta', 'No puede usar la cuenta de otra persona sin permiso', 'Nos reservamos el derecho de suspender cuentas que violen estos términos'],
                    de: ['Sie sind für die Wahrung der Vertraulichkeit Ihres Kontos verantwortlich', 'Sie müssen genaue Informationen bei der Kontoerstellung angeben', 'Sie dürfen das Konto einer anderen Person nicht ohne Erlaubnis nutzen', 'Wir behalten uns das Recht vor, Konten zu sperren, die gegen diese Bedingungen verstoßen']
                }
            },
            userContent: {
                title: {
                    en: 'User Content',
                    it: 'Contenuti degli Utenti',
                    fr: 'Contenu Utilisateur',
                    es: 'Contenido del Usuario',
                    de: 'Benutzerinhalte'
                },
                intro: {
                    en: 'When you submit ratings, comments, or other content:',
                    it: 'Quando invii valutazioni, commenti o altri contenuti:',
                    fr: 'Lorsque vous soumettez des évaluations, commentaires ou autre contenu :',
                    es: 'Cuando envía calificaciones, comentarios u otro contenido:',
                    de: 'Wenn Sie Bewertungen, Kommentare oder andere Inhalte einreichen:'
                },
                items: {
                    en: ['You grant us a non-exclusive license to display that content', 'You are responsible for the content you submit', 'Content must not be offensive, illegal, or infringe on others\' rights', 'We may remove content that violates these terms'],
                    it: ['Ci concedi una licenza non esclusiva per visualizzare quel contenuto', 'Sei responsabile del contenuto che invii', 'Il contenuto non deve essere offensivo, illegale o violare i diritti altrui', 'Potremmo rimuovere contenuti che violano questi termini'],
                    fr: ['Vous nous accordez une licence non exclusive pour afficher ce contenu', 'Vous êtes responsable du contenu que vous soumettez', 'Le contenu ne doit pas être offensant, illégal ou porter atteinte aux droits d\'autrui', 'Nous pouvons supprimer le contenu qui viole ces conditions'],
                    es: ['Nos otorga una licencia no exclusiva para mostrar ese contenido', 'Usted es responsable del contenido que envía', 'El contenido no debe ser ofensivo, ilegal o infringir los derechos de otros', 'Podemos eliminar contenido que viole estos términos'],
                    de: ['Sie gewähren uns eine nicht-exklusive Lizenz zur Anzeige dieses Inhalts', 'Sie sind für den von Ihnen eingereichten Inhalt verantwortlich', 'Inhalte dürfen nicht beleidigend, illegal sein oder die Rechte anderer verletzen', 'Wir können Inhalte entfernen, die gegen diese Bedingungen verstoßen']
                }
            },
            ip: {
                title: {
                    en: 'Intellectual Property',
                    it: 'Proprietà Intellettuale',
                    fr: 'Propriété Intellectuelle',
                    es: 'Propiedad Intelectual',
                    de: 'Geistiges Eigentum'
                },
                text: {
                    en: 'The content, organization, graphics, design, and other matters related to Sous Chef Linguine are protected under applicable copyrights and other proprietary laws. Traditional recipes may be in the public domain, but our presentation, descriptions, and AI-generated content are proprietary.',
                    it: 'Il contenuto, l\'organizzazione, la grafica, il design e altre questioni relative a Sous Chef Linguine sono protetti dalle leggi sul copyright applicabili e da altre leggi proprietarie. Le ricette tradizionali potrebbero essere di dominio pubblico, ma la nostra presentazione, le descrizioni e i contenuti generati dall\'IA sono proprietari.',
                    fr: 'Le contenu, l\'organisation, les graphiques, le design et autres éléments liés à Sous Chef Linguine sont protégés par les lois sur le droit d\'auteur et autres lois propriétaires applicables. Les recettes traditionnelles peuvent être dans le domaine public, mais notre présentation, nos descriptions et notre contenu généré par l\'IA sont propriétaires.',
                    es: 'El contenido, organización, gráficos, diseño y otros asuntos relacionados con Sous Chef Linguine están protegidos por las leyes de derechos de autor aplicables y otras leyes de propiedad. Las recetas tradicionales pueden ser de dominio público, pero nuestra presentación, descripciones y contenido generado por IA son propietarios.',
                    de: 'Der Inhalt, die Organisation, Grafiken, das Design und andere Angelegenheiten im Zusammenhang mit Sous Chef Linguine sind durch anwendbare Urheberrechtsgesetze und andere Eigentumsgesetze geschützt. Traditionelle Rezepte können gemeinfrei sein, aber unsere Präsentation, Beschreibungen und KI-generierten Inhalte sind Eigentum.'
                }
            },
            liability: {
                title: {
                    en: 'Limitation of Liability',
                    it: 'Limitazione di Responsabilità',
                    fr: 'Limitation de Responsabilité',
                    es: 'Limitación de Responsabilidad',
                    de: 'Haftungsbeschränkung'
                },
                text: {
                    en: 'Sous Chef Linguine shall not be liable for any damages resulting from the use or inability to use our service, including but not limited to damages from food preparation, allergic reactions, or incorrect recipe information.',
                    it: 'Sous Chef Linguine non sarà responsabile per eventuali danni derivanti dall\'uso o dall\'impossibilità di utilizzare il nostro servizio, inclusi ma non limitati a danni dalla preparazione di cibo, reazioni allergiche o informazioni errate sulle ricette.',
                    fr: 'Sous Chef Linguine ne sera pas responsable des dommages résultant de l\'utilisation ou de l\'impossibilité d\'utiliser notre service, y compris mais sans s\'y limiter les dommages liés à la préparation alimentaire, aux réactions allergiques ou aux informations de recette incorrectes.',
                    es: 'Sous Chef Linguine no será responsable de ningún daño que resulte del uso o la imposibilidad de usar nuestro servicio, incluyendo pero no limitado a daños por preparación de alimentos, reacciones alérgicas o información incorrecta de recetas.',
                    de: 'Sous Chef Linguine haftet nicht für Schäden, die aus der Nutzung oder der Unfähigkeit zur Nutzung unseres Dienstes entstehen, einschließlich aber nicht beschränkt auf Schäden durch Nahrungszubereitung, allergische Reaktionen oder falsche Rezeptinformationen.'
                }
            },
            changes: {
                title: {
                    en: 'Changes to Terms',
                    it: 'Modifiche ai Termini',
                    fr: 'Modifications des Conditions',
                    es: 'Cambios en los Términos',
                    de: 'Änderungen der Bedingungen'
                },
                text: {
                    en: 'We reserve the right to modify these terms at any time. Changes will be posted on this page with an updated revision date. Continued use of the service after changes constitutes acceptance of the new terms.',
                    it: 'Ci riserviamo il diritto di modificare questi termini in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con una data di revisione aggiornata. L\'uso continuato del servizio dopo le modifiche costituisce accettazione dei nuovi termini.',
                    fr: 'Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications seront publiées sur cette page avec une date de révision mise à jour. L\'utilisation continue du service après les modifications constitue l\'acceptation des nouvelles conditions.',
                    es: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios se publicarán en esta página con una fecha de revisión actualizada. El uso continuado del servicio después de los cambios constituye la aceptación de los nuevos términos.',
                    de: 'Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Änderungen werden auf dieser Seite mit einem aktualisierten Revisionsdatum veröffentlicht. Die fortgesetzte Nutzung des Dienstes nach Änderungen stellt die Annahme der neuen Bedingungen dar.'
                }
            },
            contact: {
                title: {
                    en: 'Contact',
                    it: 'Contatto',
                    fr: 'Contact',
                    es: 'Contacto',
                    de: 'Kontakt'
                },
                text: {
                    en: 'For questions about these Terms of Service, please contact us through our',
                    it: 'Per domande su questi Termini di Servizio, contattaci tramite la nostra',
                    fr: 'Pour des questions sur ces Conditions d\'Utilisation, veuillez nous contacter via notre',
                    es: 'Para preguntas sobre estos Términos de Servicio, contáctenos a través de nuestra',
                    de: 'Bei Fragen zu diesen Nutzungsbedingungen kontaktieren Sie uns bitte über unsere'
                },
                link: {
                    en: 'Contact page',
                    it: 'pagina Contatti',
                    fr: 'page Contact',
                    es: 'página de Contacto',
                    de: 'Kontaktseite'
                }
            }
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF7F0]">
            <PageSEO title="Terms of Service" description="Terms and conditions for using the Sous Chef Linguine recipe platform." path="/terms" />
            {/* Header */}
            <section className="bg-gradient-to-b from-[#F5F2E8] to-[#FAF7F0] py-12 px-4">
                <div className="max-w-4xl mx-auto">
                    <nav className="flex items-center gap-2 text-sm text-[#1E1E1E]/60 mb-6">
                        <Link to="/" className="hover:text-[#6A1F2E] flex items-center gap-1">
                            <Home className="h-4 w-4" /> {t('common.home', language)}
                        </Link>
                        <ChevronRight className="h-4 w-4" />
                        <span className="text-[#6A1F2E] font-medium">{t('terms.title', language)}</span>
                    </nav>
                    <h1 className="text-4xl sm:text-5xl font-bold text-[#1E1E1E]" style={{ fontFamily: 'var(--font-heading)' }}>
                        {t('terms.title', language)}
                    </h1>
                    <p className="text-[#1E1E1E]/70 mt-4">{t('terms.lastUpdated', language)}</p>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
                    
                    {/* Section 1: Acceptance */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            1. {content.sections.acceptance.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            {content.sections.acceptance.text[language]}
                        </p>
                    </section>

                    {/* Section 2: Description */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            2. {content.sections.description.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            {content.sections.description.text[language]}
                        </p>
                    </section>

                    {/* Section 3: AI Disclaimer */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            3. {content.sections.aiDisclaimer.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed mb-4">
                            {content.sections.aiDisclaimer.intro[language]}
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-[#1E1E1E]/80">
                            {content.sections.aiDisclaimer.items[language].map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </section>

                    {/* Section 4: User Accounts */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            4. {content.sections.userAccounts.title[language]}
                        </h2>
                        <ul className="list-disc pl-6 space-y-2 text-[#1E1E1E]/80">
                            {content.sections.userAccounts.items[language].map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </section>

                    {/* Section 5: User Content */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            5. {content.sections.userContent.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed mb-4">
                            {content.sections.userContent.intro[language]}
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-[#1E1E1E]/80">
                            {content.sections.userContent.items[language].map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </section>

                    {/* Section 6: IP */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            6. {content.sections.ip.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            {content.sections.ip.text[language]}
                        </p>
                    </section>

                    {/* Section 7: Liability */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            7. {content.sections.liability.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            {content.sections.liability.text[language]}
                        </p>
                    </section>

                    {/* Section 8: Changes */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            8. {content.sections.changes.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            {content.sections.changes.text[language]}
                        </p>
                    </section>

                    {/* Section 9: Contact */}
                    <section>
                        <h2 className="text-2xl font-bold text-[#6A1F2E] mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                            9. {content.sections.contact.title[language]}
                        </h2>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            {content.sections.contact.text[language]} <Link to="/contact" className="text-[#6A1F2E] hover:underline">{content.sections.contact.link[language]}</Link>.
                        </p>
                    </section>

                </div>
            </section>
        </div>
    );
};

export default TermsPage;
